import "./App.css";
import "katex/dist/katex.min.css";
import { ImageKitProvider } from "@imagekit/react";
import { IK_ENDPOINT } from "./helpers/imagekit";
import Page404 from "./components/pages/auth/Page404.js";
import WebsiteRoot from "./components/pages/website/WebsiteRoot";
import WebsitePageRenderer from "./components/pages/website/WebsitePageRenderer";
import WebsitePostRenderer from "./components/pages/website/WebsitePostRenderer";
import WebsiteBlogIndex from "./components/pages/website/WebsiteBlogIndex";
import CmsLayout from "./components/pages/cms/CmsLayout";
import CmsDashboard from "./components/pages/cms/CmsDashboard";
import CmsSettings from "./components/pages/cms/CmsSettings";
import CmsPagesManage from "./components/pages/cms/CmsPagesManage";
import CmsPostsManage from "./components/pages/cms/CmsPostsManage";
import CmsCategories from "./components/pages/cms/CmsCategories";
import CmsContentEditor from "./components/pages/cms/CmsContentEditor";
import { useEffect, useState } from "react";
import { getApiBaseUrl } from "./helpers/apiUrl";
import { ThemeProvider } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import { Box, CircularProgress, Stack, Typography } from "@mui/material";
import {
  selectCurrentUser,
  logout,
  login,
  setAccessToken,
  setUser,
} from "./components/pages/auth/authSlice.js";
import { useSelector, useDispatch } from "react-redux";
import {
  Routes,
  Route,
  Navigate,
  Outlet,
  useLocation,
  useNavigate,
} from "react-router-dom";
import {
  Dashboard,
  Classes,
  Staff,
  Subjects,
  Roles,
  Settings,
  Students,
  Login,
  ForgotPassword,
  ResetPassword,
  ForcePasswordChange,
  ClassBroadsheet,
  SubjectReconciliation,
  ClassReport,
  StudentReport,
  CAManager,
  QuickEntry,
  ExamManager,
  ManageAttendance,
  ManageClassStudents,
  EditProfile,
  Houses,
  ClassSubjects,
  ClassReportSheet,
  AccountSettings,
  FeesSchedule,
  Invoices,
  PaymentsDashboard,
  Templates,
  SchoolImport,
  SchoolInformation,
  ParentDashboard,
  ParentStudentDetail,
  ScoreLog,
  ActivityLogs,
  LmsOverview,
  LmsNotes,
  LmsMediaLibrary,
  LmsReviews,
  StudentOverview,
  StudentNotes,
  StudentClassQuiz,
  StudentQuiz,
  SubscriptionPage,
  SessionTransfer,
  CommunicationCenter,
  FeedPage,
  MessagesPage,
  NotificationSettings,
} from "./components/pages/index";
import Layout from "./components/layout/Layout";
import LmsLayout from "./components/layout/LmsLayout";
import CommunicationLayout from "./components/layout/CommunicationLayout";
import ParentLayout from "./components/layout/ParentLayout";
import NotesRouter from "./components/pages/lms/teacher/notes/NotesRouter";
import QuizzesRouter from "./components/pages/lms/teacher/quizzes/QuizzesRouter";
import QuestionsRouter from "./components/pages/lms/teacher/questions/QuestionsRouter";
import ReviewsRouter from "./components/pages/lms/teacher/reviews/ReviewsRouter";
import ReviewerOverview from "./components/pages/lms/reviewer/ReviewerOverview";
import StudentNotesRouter from "./components/pages/lms/student/notes/StudentNotesRouter";
import StudentClassQuizRouter from "./components/pages/lms/student/class-quiz/StudentClassQuizRouter";
import StudentQuizRouter from "./components/pages/lms/student/quiz/StudentQuizRouter";
import StudentLeaderboard from "./components/pages/lms/student/leaderboard";
import TeacherLeaderboard from "./components/pages/lms/teacher/leaderboard";
import SectionReport from "./components/pages/SectionReport/SectionReport";
import DepartmentReport from "./components/pages/DepartmentReport/DepartmentReport";
import toast from "./helpers/toast";
import ToastContainer from "./components/common/ToastContainer";
import useTheme from "./theme/theme";
import { setColor } from "./theme/themeSlice";
import { authApiSlice } from "./app/authApiSlice";
import TitleManager from "./components/common/TitleManager";
import { useNetworkStatus } from "./hooks/useNetworkStatus";
import { useIsPro } from "./hooks/useIsPro";
import { useWebPushNotifications } from "./hooks/useWebPushNotifications";
import MobileAppDownloadPrompt from "./components/common/MobileAppDownloadPrompt";

const STAFF_ROLES = ["admin", "teacher", "agent", "accountant", "examiner"];

const ProtectedRoute = ({
  user,
  allowedRoles,
  children,
  requiresPro = false,
}) => {
  //wrapper component for protected routes
  const dispatch = useDispatch();
  const isPro = useIsPro();
  const isAuth = !!user;

  const userRoles = user?.roles
    ?.map((item) => item?.role?.name)
    .filter(Boolean);

  const hasRequiredRole = userRoles?.filter((role) =>
    allowedRoles.includes(role),
  )?.length
    ? true
    : false;

  if (!isAuth) {
    return <Navigate to="/login" replace />;
  }

  //log the user out if they dont have any roles
  if (!userRoles?.length) {
    toast.error("Sorry, user doesn't have any role");
    return dispatch(logout());
  }

  //prevent the user from galavanting
  if (!hasRequiredRole) {
    toast.error("Access Denied");
    return dispatch(logout());
  }

  // Check pro subscription requirement
  if (requiresPro && !isPro) {
    toast.error("This feature requires a Pro subscription");
    return dispatch(logout());
  }

  // Force staff users to change their password on first login
  const isStaff = userRoles.some((role) => STAFF_ROLES.includes(role));
  if (
    isStaff &&
    (user?.force_password_change || user?.force_password_change == null)
  ) {
    return <Navigate to="/change-password" replace />;
  }

  return children;
};

const NoNav = () => (
  <>
    <Outlet />
  </>
);

function normalizeSchoolColor(colorValue) {
  if (!colorValue) return null;

  if (typeof colorValue === "string") {
    return colorValue;
  }

  if (typeof colorValue === "object") {
    return (
      colorValue.value ||
      colorValue.hex ||
      colorValue.main ||
      colorValue.color ||
      null
    );
  }

  return null;
}

const AppBootLoader = () => (
  <Box
    sx={{
      minHeight: "100vh",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      px: 3,
      background:
        "radial-gradient(circle at top, rgba(37, 116, 209, 0.08), transparent 42%), #f6f8fb",
    }}
  >
    <Stack
      spacing={2}
      alignItems="center"
      sx={{
        width: "100%",
        maxWidth: 320,
        py: 5,
        px: 4,
        borderRadius: 4,
        backgroundColor: "rgba(255, 255, 255, 0.92)",
        boxShadow: "0 18px 45px rgba(15, 23, 42, 0.08)",
        backdropFilter: "blur(8px)",
      }}
    >
      <Box
        component="img"
        src="/logo192.png"
        alt="Schoola"
        sx={{ width: 88, height: 88, objectFit: "contain" }}
      />
      <Stack spacing={0.5} alignItems="center">
        <Typography
          variant="h6"
          sx={{ fontWeight: 800, letterSpacing: "0.06em" }}
        >
          SCHOOLA
        </Typography>
        <Typography variant="body2" color="text.secondary" textAlign="center">
          Loading your school portal
        </Typography>
      </Stack>
      <CircularProgress size={28} sx={{ color: "#5f6b7a" }} />
    </Stack>
  </Box>
);

function AppRoutes() {
  //all the routes
  const user = useSelector(selectCurrentUser);
  const dispatch = useDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const pathname = location?.pathname || "/";
  const [isAuthLoaded, setIsAuthLoaded] = useState(false);
  const { isOnline } = useNetworkStatus();
  useWebPushNotifications();

  useEffect(() => {
    //check saved user in local storage
    const userString = localStorage.getItem("user");
    const access_tokenString = localStorage.getItem("access_token");
    const refresh_tokenString = localStorage.getItem("refresh_token");
    const color = localStorage.getItem("color");

    const initAuth = async () => {
      if (color) {
        const parsedColor = JSON.parse(color);
        dispatch(setColor({ color: parsedColor }));
      }

      const publicInfoTask = (async () => {
        const schoolInfoRequest = dispatch(
          authApiSlice.endpoints.getPublicSchoolInfo.initiate(undefined, {
            forceRefetch: true,
          }),
        );

        try {
          const schoolInfo = await schoolInfoRequest.unwrap();
          const schoolColor = normalizeSchoolColor(schoolInfo?.color);
          if (schoolColor) {
            dispatch(setColor({ color: schoolColor }));
          }
        } catch (_) {
          // If branding fails, continue boot with persisted/default theme.
        } finally {
          schoolInfoRequest.unsubscribe();
        }
      })();

      const authTask = (async () => {
        if (userString && access_tokenString && refresh_tokenString) {
          const user = JSON.parse(userString);
          const access_token = JSON.parse(access_tokenString);
          const refresh_token = JSON.parse(refresh_tokenString);
          dispatch(login({ user, access_token, refresh_token }));
          // Silent token refresh to get a fresh access token with current roles
          try {
            const BASE_URL = getApiBaseUrl();
            const refreshRes = await fetch(`${BASE_URL}/auth/token-refresh`, {
              method: "POST",
              headers: { Authorization: `Bearer ${refresh_token}` },
            });
            if (refreshRes.ok) {
              const data = await refreshRes.json();
              if (data.access_token)
                dispatch(setAccessToken({ access_token: data.access_token }));
              if (data.user) dispatch(setUser(data.user));
            }
          } catch (_) {
            // silent fail — user stays logged in with existing token
          }
        }
      })();

      await Promise.allSettled([authTask, publicInfoTask]);
      setIsAuthLoaded(true);
    };

    initAuth();
  }, [dispatch]);

  useEffect(() => {
    if (!!user && pathname === "/login") {
      const roles = user?.roles?.map((r) => r?.role?.name).filter(Boolean);
      const isParentOnly =
        roles?.length > 0 && roles.every((r) => r === "parent");
      const isStudentOnly =
        roles?.length > 0 && roles.every((r) => r === "student");
      const isReviewerOnly =
        roles?.length > 0 && roles.every((r) => r === "reviewer");
      navigate(
        isParentOnly
          ? "/parent"
          : isStudentOnly
            ? "/student"
            : isReviewerOnly
              ? "/lms/overview"
              : "/dashboard",
      );
    }
    // Redirect away from /change-password only once the flag is explicitly cleared
    if (
      !!user &&
      user?.force_password_change === false &&
      pathname === "/change-password"
    ) {
      navigate("/dashboard");
    }
  }, [pathname, user, navigate]);

  useEffect(() => {
    if (isOnline) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
      return "";
    };

    const handleKeyDown = (event) => {
      const isReloadShortcut =
        event.key === "F5" ||
        ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "r");

      if (!isReloadShortcut) return;

      event.preventDefault();
      toast.info(
        "Refresh is blocked while offline to avoid losing cached report-entry context.",
      );
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isOnline]);

  if (!isAuthLoaded) {
    return <AppBootLoader />;
  }

  return (
    <>
      <TitleManager />
      <MobileAppDownloadPrompt />
      <Routes>
        {/* Public website routes — no auth required */}
        <Route path="/" element={<WebsiteRoot />} />
        <Route path="/p/:slug" element={<WebsitePageRenderer />} />
        <Route path="/blog" element={<WebsiteBlogIndex />} />
        <Route
          path="/blog/category/:categorySlug"
          element={<WebsiteBlogIndex />}
        />
        <Route path="/blog/:slug" element={<WebsitePostRenderer />} />

        {/* CMS management routes — webmaster + admin (Pro subscription required) */}
        <Route
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={["webmaster", "admin"]}
              requiresPro={true}
            >
              <CmsLayout />
            </ProtectedRoute>
          }
        >
          <Route path="cms" element={<CmsDashboard />} />
          <Route path="cms/pages" element={<CmsPagesManage />} />
          <Route
            path="cms/pages/new"
            element={<CmsContentEditor type="page" />}
          />
          <Route
            path="cms/pages/:id/edit"
            element={<CmsContentEditor type="page" />}
          />
          <Route path="cms/posts" element={<CmsPostsManage />} />
          <Route
            path="cms/posts/new"
            element={<CmsContentEditor type="post" />}
          />
          <Route
            path="cms/posts/:id/edit"
            element={<CmsContentEditor type="post" />}
          />
          <Route path="cms/categories" element={<CmsCategories />} />
          <Route path="cms/settings" element={<CmsSettings />} />
          <Route path="cms/navigation" element={<CmsSettings />} />
        </Route>

        <Route element={<NoNav />}>
          {/* public routes that dont have the sidebar nav */}
          <Route path="login" element={<Login />} />
          <Route path="forgot-password" element={<ForgotPassword />} />
          <Route path="reset-password" element={<ResetPassword />} />
          <Route
            path="change-password"
            element={
              user ? <ForcePasswordChange /> : <Navigate to="/login" replace />
            }
          />
          <Route path="*" element={<Page404 />} />
        </Route>

        <Route
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={[
                "admin",
                "teacher",
                "accountant",
                "agent",
                "examiner",
                "parent",
                "reviewer",
              ]}
            >
              <Layout />
            </ProtectedRoute>
          }
        >
          {/* shared routes by both admin, accounant and teacher */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="dashboard" element={<Dashboard />} />
          <Route path="dashboard/edit-profile" element={<EditProfile />} />
        </Route>
        <Route
          element={
            <ProtectedRoute user={user} allowedRoles={["admin"]}>
              <Layout />
            </ProtectedRoute>
          }
        >
          {/* routes strictly for admin */}
          <Route path="classes" element={<Classes />} />
          <Route path="staff" element={<Staff />} />
          <Route path="subjects" element={<Subjects />} />
          <Route path="roles" element={<Roles />} />
          <Route path="students" element={<Students />} />
          <Route path="settings" element={<Settings />} />
          <Route path="score-log" element={<ScoreLog />} />
          <Route path="activity-logs" element={<ActivityLogs />} />
          <Route path="session-transfer" element={<SessionTransfer />} />
        </Route>

        <Route
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={["teacher", "admin", "examiner"]}
            >
              <Layout />
            </ProtectedRoute>
          }
        >
          {/* routes for teacher, admin, and examiner */}
          <Route path="class-broadsheet" element={<ClassBroadsheet />} />
          <Route path="class-report" element={<ClassReport />} />
          <Route path="student-report" element={<StudentReport />} />
          <Route path="ca-manager" element={<CAManager />} />
          <Route path="quick-entry" element={<QuickEntry />} />
          <Route path="exam-manager" element={<ExamManager />} />
          <Route path="manage-attendance" element={<ManageAttendance />} />
          <Route
            path="class-students/:classId"
            element={<ManageClassStudents />}
          />
          <Route path="houses" element={<Houses />} />
          <Route path="class-subjects/:classId" element={<ClassSubjects />} />
          <Route
            path="class-reportsheet/:classId"
            element={<ClassReportSheet />}
          />
          <Route path="section-report/:sectionId" element={<SectionReport />} />
          <Route
            path="department-report/:departmentId"
            element={<DepartmentReport />}
          />
        </Route>

        <Route
          element={
            <ProtectedRoute user={user} allowedRoles={["examiner"]}>
              <Layout />
            </ProtectedRoute>
          }
        >
          {/* routes for examiner only */}
          <Route path="subject-reconciliation" element={<SubjectReconciliation />} />
        </Route>

        {/* LMS Routes with separate layout */}
        <Route
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={["teacher", "admin", "examiner"]}
            >
              <LmsLayout />
            </ProtectedRoute>
          }
        >
          <Route path="lms/overview" element={<LmsOverview />} />
          <Route path="lms/notes/*" element={<NotesRouter />} />
          {/* <Route path="lms/media-library" element={<LmsMediaLibrary />} /> */}
          <Route path="lms/questions/*" element={<QuestionsRouter />} />
          <Route path="lms/quizzes/*" element={<QuizzesRouter />} />
          <Route path="lms/leaderboard" element={<TeacherLeaderboard />} />
        </Route>

        {/* Communication — standalone layout for all roles */}
        <Route
          element={
            <ProtectedRoute
              user={user}
              allowedRoles={[
                "admin",
                "teacher",
                "accountant",
                "agent",
                "examiner",
                "parent",
              ]}
            >
              <CommunicationLayout />
            </ProtectedRoute>
          }
        >
          <Route
            path="communication"
            element={<Navigate to="/communication/feed" replace />}
          />
          <Route path="communication/feed" element={<FeedPage />} />
          <Route path="communication/messages" element={<MessagesPage />} />
          <Route
            path="communication/messages/:conversationId"
            element={<MessagesPage />}
          />
          <Route
            path="communication/settings"
            element={<NotificationSettings />}
          />
          {/* Legacy parent redirect */}
          <Route
            path="parent/communication"
            element={<Navigate to="/communication/feed" replace />}
          />
        </Route>

        {/* Reviewer LMS Routes */}
        <Route
          element={
            <ProtectedRoute user={user} allowedRoles={["reviewer"]}>
              <LmsLayout />
            </ProtectedRoute>
          }
        >
          <Route path="lms/overview" element={<ReviewerOverview />} />
          <Route path="lms/reviews/*" element={<ReviewsRouter />} />
        </Route>

        <Route
          element={
            <ProtectedRoute user={user} allowedRoles={["accountant"]}>
              <Layout />
            </ProtectedRoute>
          }
        >
          {/* routes strictly for accountant */}
          <Route path="account-settings" element={<AccountSettings />} />
          <Route path="fees-schedule" element={<FeesSchedule />} />
          <Route path="invoices" element={<Invoices />} />
          <Route path="payments-dashboard" element={<PaymentsDashboard />} />
        </Route>

        <Route
          element={
            <ProtectedRoute user={user} allowedRoles={["agent"]}>
              <Layout />
            </ProtectedRoute>
          }
        >
          {/* routes strictly for agent */}
          <Route path="templates" element={<Templates />} />
          <Route path="school-import" element={<SchoolImport />} />
          <Route path="school-information" element={<SchoolInformation />} />
        </Route>

        <Route
          element={
            <ProtectedRoute user={user} allowedRoles={["admin", "accountant"]}>
              <Layout />
            </ProtectedRoute>
          }
        >
          {/* Subscription management — admin + accountant */}
          <Route path="subscription" element={<SubscriptionPage />} />
        </Route>

        <Route
          element={
            <ProtectedRoute user={user} allowedRoles={["parent"]}>
              <ParentLayout />
            </ProtectedRoute>
          }
        >
          {/* routes strictly for parent */}
          <Route path="parent" element={<ParentDashboard />} />
          <Route
            path="parent/student/:studentId"
            element={<ParentStudentDetail />}
          />
        </Route>

        <Route
          path="student"
          element={
            <ProtectedRoute user={user} allowedRoles={["student"]}>
              <Navigate to="/lms/student/overview" replace />
            </ProtectedRoute>
          }
        />

        {/* Student LMS Routes */}
        <Route
          element={
            <ProtectedRoute user={user} allowedRoles={["student"]}>
              <LmsLayout />
            </ProtectedRoute>
          }
        >
          <Route path="lms/student/overview" element={<StudentOverview />} />
          <Route
            path="lms/student/leaderboard"
            element={<StudentLeaderboard />}
          />
          <Route path="lms/student/notes/*" element={<StudentNotesRouter />} />
          <Route
            path="lms/student/class-quiz/*"
            element={<StudentClassQuizRouter />}
          />
          <Route path="lms/student/quiz/*" element={<StudentQuizRouter />} />
        </Route>
      </Routes>
    </>
  );
}

function App() {
  const theme = useTheme();

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ImageKitProvider urlEndpoint={IK_ENDPOINT}>
        <div className="App">
          <AppRoutes />
          <ToastContainer />
        </div>
      </ImageKitProvider>
    </ThemeProvider>
  );
}

export default App;
